import { computed } from "vue";

export const BLOCK_DND_COOLDOWN_MS = 400;

let hideDropZonesTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Shared drag-and-drop state for the resource designer.
 *
 * Two mutually-exclusive drag "modes" are tracked via global `useState`:
 *  - `draggedTemplate`: a NEW block being dragged in from the Blocks sidebar
 *    palette (the full palette entry, exposing `.template` and `.type`).
 *  - `draggedItemId`: an EXISTING block being moved within the designer. This
 *    shares the same `useState("draggedItemId")` key used by `useDragManager`.
 *
 * Drop targets reveal themselves while `isDragActive` is true and decide whether
 * to add a new block or move an existing one based on which value is set.
 */
export const useBlockDnd = () => {
  const draggedTemplate = useState<any | null>("block-dnd-template", () => null);
  const draggedItemId = useState<string | null>("draggedItemId", () => null);
  const dropZonesVisible = useState<boolean>(
    "block-dnd-drop-zones-visible",
    () => false,
  );
  const activeDropZoneId = useState<string | null>(
    "block-dnd-active-drop-zone",
    () => null,
  );

  const isDragActive = computed(
    () => draggedTemplate.value !== null || draggedItemId.value !== null,
  );

  const cancelHideDropZones = () => {
    if (hideDropZonesTimer === null) return;
    clearTimeout(hideDropZonesTimer);
    hideDropZonesTimer = null;
  };

  const showDropZones = () => {
    cancelHideDropZones();
    dropZonesVisible.value = true;
  };

  /** Begin dragging a new block from the palette. */
  const startBlockDrag = (block: any) => {
    draggedItemId.value = null;
    draggedTemplate.value = block;
    activeDropZoneId.value = null;
    showDropZones();
  };

  /** Begin moving an existing block in the designer. */
  const startItemDrag = (itemId: string) => {
    draggedTemplate.value = null;
    draggedItemId.value = itemId;
    activeDropZoneId.value = null;
    showDropZones();
  };

  /**
   * Disable dropping immediately, but leave eligible zones visible briefly so
   * drag completion does not make a busy resource snap back all at once.
   */
  const endBlockDrag = () => {
    draggedTemplate.value = null;
    draggedItemId.value = null;
    activeDropZoneId.value = null;

    cancelHideDropZones();
    hideDropZonesTimer = setTimeout(() => {
      dropZonesVisible.value = false;
      hideDropZonesTimer = null;
    }, BLOCK_DND_COOLDOWN_MS);
  };

  const setActiveDropZone = (id: string | null) => {
    activeDropZoneId.value = id;
  };

  return {
    draggedTemplate,
    draggedItemId,
    isDragActive,
    dropZonesVisible,
    activeDropZoneId,
    startBlockDrag,
    startItemDrag,
    endBlockDrag,
    setActiveDropZone,
  };
};
